import { ReactNode } from "react";
import { NavLink } from "react-router";
import { ArrowLeft, RefreshCw } from "lucide-react";
import t from "@/lang/lang";
import { SampleBaseData } from "@/services/backend/samples/types/sample-base-data";
import { CompanyName } from "@/services/backend/company/components/company-name";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Button } from "@/shared/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";

/**
 * The frame of a sample base data detail route: a link back to the list,
 * the label and company, the persistence state and the given actions. The
 * fields of the kind are passed as children.
 */
export function SampleBaseDataDetailCard({
  baseData,
  isLoading,
  error,
  actions,
  children,
}: {
  baseData: SampleBaseData | undefined;
  isLoading: boolean;
  error: unknown;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading || !baseData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex space-x-4">
            <RefreshCw className="animate-spin" />
            <span>{t("Lade Basisdaten")}</span>
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <NavLink to="./..">
            <Button variant="outline" size="sm" className="m-0 mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              <span>{t("Übersicht")}</span>
            </Button>
          </NavLink>
          <div>{baseData.label}</div>
        </CardTitle>
        <CardDescription>
          <CompanyName companyId={baseData.companyId} />
        </CardDescription>
        <CardAction className="flex gap-3">
          <PersistenceStateUsingContext />
          {actions}
        </CardAction>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
